// react and js
import { React } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// mui
import { Box, Link } from "@mui/material";

// components

// hooks

// i18n
import i18n from "../i18n";

// layouts

// pages

// theme

// utils

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
// Map of known backend messages (lowercased + trimmed) to semantic i18n keys
// under the `backendMessages` namespace. To add a new translatable backend
// message, add an entry here AND add the matching key to every locale file
// under `backendMessages.<key>`. Unknown messages fall through to the original
// capitalize-first-letter behaviour so nothing breaks if the backend changes.
const BACKEND_MESSAGE_KEYS = {
  "username or password incorrect. after 5 failed login attempt your login will be blocked for 24 hours!":
    "loginIncorrectLockoutWarning",
  "username already taken!": "usernameAlreadyTaken",
  "verification failed, please reload the page and try again!":
    "recaptchaVerificationFailed",
  "at least one paid subscription is still active. please cancel all subscriptions before deleting your account!":
    "activeSubscriptionsBlockDelete",
  success: "success",
};

// Patterns for backend messages that include dynamic values. Each entry is a
// regex (matched against the trimmed backend message; case-insensitive so the
// captures retain their original casing) plus the i18n key and a function to
// turn the regex match into i18next interpolation values.
const BACKEND_MESSAGE_PATTERNS = [
  {
    pattern:
      /^login blocked, please wait (.+?) until next login attempt or reset password!$/i,
    tKey: "loginBlockedWait",
    capture: (m) => ({ duration: m[1] }),
  },
  {
    pattern:
      /^sorry, we do not accept this type of email address \((.+?)\)!$/i,
    tKey: "emailTypeNotAccepted",
    capture: (m) => ({ pattern: m[1] }),
  },
];

function translateBackendMessage(rawMessage) {
  if (!rawMessage) return "";
  const trimmed = rawMessage.trim();
  const normalized = trimmed.toLowerCase();

  // 1. Exact-match lookup.
  const semanticKey = BACKEND_MESSAGE_KEYS[normalized];
  if (semanticKey) {
    const translated = i18n.t(`backendMessages.${semanticKey}`);
    // i18next returns the key as-is when no translation exists; treat that
    // as a miss so we still get the readable fallback.
    if (translated && translated !== `backendMessages.${semanticKey}`) {
      return translated;
    }
  }

  // 2. Pattern-match with interpolation captures.
  for (let i = 0; i < BACKEND_MESSAGE_PATTERNS.length; i += 1) {
    const entry = BACKEND_MESSAGE_PATTERNS[i];
    const match = trimmed.match(entry.pattern);
    if (match) {
      const values = entry.capture(match);
      const translated = i18n.t(`backendMessages.${entry.tKey}`, values);
      if (translated && translated !== `backendMessages.${entry.tKey}`) {
        return translated;
      }
    }
  }

  return rawMessage.charAt(0).toUpperCase() + rawMessage.slice(1);
}

// Countries whose clients get the Mexican CFDI (factura) note. The billing
// address stores the country as the human readable label from CountrySelect
// ("Mexico"), but older/backend-sourced records may carry the ISO code or the
// accented spelling, so normalise and match all of them.
const MEXICO_COUNTRY_VALUES = ["mexico", "méxico", "mx"];

// True when the given billing country belongs to Mexico.
export function isMexicanCountry(country) {
  return MEXICO_COUNTRY_VALUES.includes(
    String(country || "")
      .trim()
      .toLowerCase()
  );
}

// Matches a plain email address embedded in free text. Kept non-global on
// purpose (a global regex carries `lastIndex` between calls); findEmailsInText
// builds its own global copy per call so callers can't trip over shared state.
const EMAIL_IN_TEXT = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;

// Locates every email address inside a piece of text. Returns the matched
// address plus its offset, so both the HTML and the PDF renderer can turn the
// address into a mailto link without the address being duplicated in code —
// whatever the (translated) text says is what gets linked.
export function findEmailsInText(text) {
  const source = String(text || "");
  const pattern = new RegExp(EMAIL_IN_TEXT.source, "g");
  const matches = [];

  let match = pattern.exec(source);
  while (match !== null) {
    matches.push({ value: match[0], index: match.index });
    match = pattern.exec(source);
  }

  return matches;
}

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
// Renders a backend-returned message, re-translating on every language
// change. Use this (rather than calling translateBackendMessage eagerly and
// storing the string in state) so messages currently on screen swap to the
// new language when the selector is toggled.
export function BackendMessage({ raw, expectedPattern }) {
  // Subscribing via useTranslation forces a re-render on language change.
  useTranslation();
  const formattedMessage = translateBackendMessage(raw);
  if (expectedPattern) {
    let patterns = "";
    expectedPattern.forEach((pattern) => {
      patterns += `${pattern}, `;
    });
    return (
      <>
        {formattedMessage}
        <Box sx={{ m: 1 }} />
        {patterns.substring(0, patterns.length - 2)}
      </>
    );
  }
  return formattedMessage;
}

BackendMessage.propTypes = {
  raw: PropTypes.string,
  expectedPattern: PropTypes.arrayOf(PropTypes.string),
};

// Renders the translation of an i18n key, reactive to language changes.
// Use this when storing a translated message into React state — pass this
// element instead of the resolved string so the displayed text follows the
// active language even while sitting in state.
export function TranslatedMessage({ tKey }) {
  const { t } = useTranslation();
  return <>{t(tKey)}</>;
}

TranslatedMessage.propTypes = {
  tKey: PropTypes.string.isRequired,
};

export function combineReturnMessage(content, setReturnMessage) {
  setReturnMessage(
    <BackendMessage
      raw={content.message}
      expectedPattern={content.expected_pattern}
    />
  );
}

// Renders a piece of text with every email address in it turned into a mailto
// link, leaving the surrounding wording untouched. Styled like every other
// link in the project: bold, no underline, turning green on hover (that comes
// from the `*Link` typography variants).
export function TextWithMailtoLinks({ text, variant }) {
  const source = String(text || "");
  const parts = [];
  let cursor = 0;

  findEmailsInText(source).forEach((email) => {
    if (email.index > cursor) {
      parts.push(source.slice(cursor, email.index));
    }

    parts.push(
      <Link
        key={`${email.value}-${email.index}`}
        variant={variant}
        underline="none"
        color="inherit"
        href={`mailto:${email.value}`}
      >
        {email.value}
      </Link>
    );

    cursor = email.index + email.value.length;
  });

  if (cursor < source.length) {
    parts.push(source.slice(cursor));
  }

  // Returned as an array rather than wrapped in a fragment: the segments go
  // straight into the surrounding text flow, and the links carry their keys.
  return parts;
}

TextWithMailtoLinks.propTypes = {
  text: PropTypes.string,
  variant: PropTypes.string,
};

TextWithMailtoLinks.defaultProps = {
  text: "",
  variant: "contentMiddleLink",
};
